import type React from 'react';

import { Button, Card, CardBody, CardHeader, Image, Link, ProgressBar, Text } from '@change/ds-core-ui';
import { TrendingIcon } from '@change/ds-icons';

import { TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { FEATURED_PETITION_URL } from '../../constants';
import type { FeaturedPetition } from '../../hooks/useFeaturedPetition';

type Props = Readonly<{
	petition: FeaturedPetition;
}>;

// Progress toward the goal, clamped to 100 and guarding a zero/absent goal (petitionDiscover precedent).
function computePercentage(count: number, goal: number): number {
	if (goal <= 0) return 0;
	return Math.min(100, (count / goal) * 100);
}

// Presentational surging-petition cover card. Receives the already-resolved featured petition — it
// does no fetching and no gating (both live in the container). All copy comes from the card's i18n
// placeholders; the petition title/photo are UGC and stay untranslated. Numbers and the date use the
// GraphQL pre-localized fields so they display verbatim in every locale.
export const SurgingPetitionCard: React.FC<Props> = ({ petition }) => {
	const { translate } = useI18n();
	const {
		displayTitle,
		createdAtLocalized,
		photo,
		signatureState: { signatureCount, signatureGoal },
		dailySignatureCountLocalized,
	} = petition;

	const percentage = computePercentage(signatureCount.displayed, signatureGoal.displayed);
	// Skip a missing or still-processing image (petitionDiscover CardImage precedent — never render a
	// broken/placeholder state); the card then renders without the image block and pill.
	const imageUrl = photo?.petitionMedium.processing ? undefined : photo?.petitionMedium.url;

	return (
		<Card elevation>
			{imageUrl && (
				<CardHeader variant="media" className="relative">
					{/* alt is the featured petition's title — UGC, untranslated */}
					<Image alt={displayTitle} src={imageUrl} className="w-full object-cover" />
					<span
						data-testid="uk-surging-card-badge"
						className="bg-fill-brand-strong absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full px-3 py-1"
					>
						{/* decorative — the label carries the meaning */}
						<TrendingIcon size="small" className="text-icon-inverse-strong-fixed" />
						<Text
							as="span"
							variant="body"
							size="x-small"
							fontWeight="bold"
							className="text-typography-inverse-strong-fixed"
						>
							{translate('corgi.petition.details.uk_surging_card.badge')}
						</Text>
					</span>
				</CardHeader>
			)}
			<CardBody className="space-y-3">
				<div data-testid="uk-surging-card-eyebrow" className="text-typography-brand flex items-center gap-1">
					{/* decorative — the label carries the meaning */}
					<TrendingIcon size="small" className="text-icon-brand" />
					<Text as="span" variant="body" size="x-small" fontWeight="bold" className="tracking-wide uppercase">
						{translate('corgi.petition.details.uk_surging_card.eyebrow')}
					</Text>
				</div>

				<Text variant="heading" size="large" fontWeight="bold" className="text-typography-strong">
					{displayTitle}
				</Text>

				<Text as="p" variant="body" size="small" className="text-typography-weak">
					{translate('corgi.petition.details.uk_surging_card.created_date', { date: createdAtLocalized })}
				</Text>

				<ProgressBar
					ariaLabel={translate('corgi.petition.details.uk_surging_card.progress_label')}
					percentage={percentage}
				/>

				<div className="flex items-center justify-between gap-2">
					<Text
						as="span"
						data-testid="uk-surging-card-count-of-goal"
						variant="body"
						size="small"
						className="text-typography-weak"
					>
						<TranslateHtml
							value="corgi.petition.details.uk_surging_card.count_of_goal"
							replacements={{
								count: (
									<Text as="span" fontWeight="bold" className="text-typography-strong">
										{signatureCount.displayedLocalized}
									</Text>
								),
								goal: signatureGoal.displayedLocalized,
							}}
						/>
					</Text>
					<Text as="span" variant="body" size="small" fontWeight="bold" className="text-typography-brand">
						{translate('corgi.petition.details.uk_surging_card.delta_24h', {
							delta: dailySignatureCountLocalized,
						})}
					</Text>
				</div>

				<div>
					<Button as={Link} href={FEATURED_PETITION_URL} variant="secondary" size="small" underline={false}>
						{translate('corgi.petition.details.uk_surging_card.view_petition')}
					</Button>
				</div>
			</CardBody>
		</Card>
	);
};
